import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/lib/analytics';
import { dlPageView } from '@/lib/dataLayer';

/**
 * Page Tracking Hook
 * ==================
 * Sends page_view to analytics pipeline (separate from CRM).
 * Excludes internal/admin routes to prevent funnel contamination.
 */

// ─── Internal Route Blocklist ───────────────────────────────────────────────

const INTERNAL_ROUTES = [
  '/dashboard',
  '/debug',
  '/auth',
  '/resultado',
  '/pedido-confirmado',
  '/perfil',
  '/pedidos',
  '/favoritos',
  '/afiliados/dashboard',
  '/profissional/dashboard',
  '/profissional/registro',
  '/minha-pele',
  '/relatorio',
  '/diario',
  '/seo-debug',
];

/**
 * Determines whether a pathname should be tracked in commercial analytics.
 *
 * Dashboard, debug, auth, and account-area routes are excluded because
 * their page_view / scroll_depth events contaminate the business funnel
 * (Home → PDP → Quiz → Cart → Checkout → Purchase) with non-commercial
 * traffic, skewing conversion rates and attribution in GA4 / GTM / Meta.
 */
export function shouldTrackCommercialRoute(pathname: string): boolean {
  return !INTERNAL_ROUTES.some(
    (blocked) => pathname === blocked || pathname.startsWith(blocked + '/'),
  );
}

// ─── Route → Title Mapping ──────────────────────────────────────────────────

const pageTitles: Record<string, string> = {
  '/': 'Home | NAT HOF',
  '/loja': 'Loja | NAT HOF',
  '/kits': 'Kits | NAT HOF',
  '/sobre': 'Sobre Nós | NAT HOF',
  '/ciencia-e-tecnologia': 'Ciência e Tecnologia | NAT HOF',
  '/b2b': 'Para Clínicas | NAT HOF',
  '/clinicas': 'Clínicas Parceiras | NAT HOF',
  '/quiz': 'Consulta Guiada | NAT HOF',
  '/resultado': 'Sua Rotina Personalizada | NAT HOF',
  '/auth': 'Entrar | NAT HOF',
  '/perfil': 'Meu Perfil | NAT HOF',
  '/faq': 'Perguntas Frequentes | NAT HOF',
  '/contato': 'Contato | NAT HOF',
  '/reviews': 'Avaliações | NAT HOF',
  '/politicas/privacidade': 'Política de Privacidade | NAT HOF',
  '/politicas/termos': 'Termos de Uso | NAT HOF',
  '/politicas/troca-devolucao': 'Trocas e Devoluções | NAT HOF',
  '/politicas/entrega': 'Entrega | NAT HOF',
};

const nicheTitles: Record<string, string> = {
  '/onco-care': 'Onco Care | NAT HOF',
  '/barreira-melasma': 'Barreira e Melasma | NAT HOF',
  '/inverno-baixa-umidade': 'Inverno e Baixa Umidade | NAT HOF',
  '/viagem-neve-altitude': 'Viagem, Neve e Altitude | NAT HOF',
  '/maternidade-segura': 'Maternidade Segura | NAT HOF',
  '/pele-jovem-sensivel': 'Pele Jovem e Sensível | NAT HOF',
  '/pos-sol': 'Pós-Sol | NAT HOF',
  '/menopausa-climaterio': 'Menopausa e Climatério | NAT HOF',
  '/pos-procedimentos': 'Pós-Procedimentos | NAT HOF',
  '/dermatites-pele-reativa': 'Dermatites e Pele Reativa | NAT HOF',
  '/pos-barba-homens': 'Pós-Barba e Cuidado Masculino | NAT HOF',
  '/profissionais-saude-epi': 'Profissionais de Saúde e EPI | NAT HOF',
};

// ─── Hook ───────────────────────────────────────────────────────────────────

// Dedupe em escopo de MÓDULO (não por instância): evita o segundo page_view
// quando o componente remonta (StrictMode / re-render do Router) na mesma rota.
let lastTrackedPathGlobal: string | null = null;

export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const route = location.pathname;

    // Update document title regardless of tracking
    const basePath = route.split('/').slice(0, 2).join('/');
    const title =
      pageTitles[route] ||
      nicheTitles[route] ||
      pageTitles[basePath] ||
      'NAT HOF';
    document.title = title;

    // Skip internal/admin routes
    if (!shouldTrackCommercialRoute(route)) return;

    // Deduplicate: skip if same pathname already tracked (escopo de módulo)
    if (lastTrackedPathGlobal === route) return;
    lastTrackedPathGlobal = route;

    // Wait for SEO injection / React to update document.title before dispatching
    setTimeout(() => {
      const finalTitle = document.title || title;
      // Send page_view to analytics pipeline (not CRM)
      trackPageView(route);

      // Push to dataLayer for GTM
      dlPageView(route, finalTitle);
    }, 100);
  }, [location]);
};
